/* eslint-disable react/display-name */
import React, { memo, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import Slider from 'rc-slider';

import './styles.css';
import { MapLayerPickerType, MapLayerType } from '../utilities/interfaces';
import { IconHelper } from '../icons/iconUtilities';
import { MapLayerListItem } from './mapLayerListItem';

type MapLayerListItemRefType = {
    [key: string]: HTMLLIElement | null;
}

export const MapLayerPicker = memo(({
    headerImage,
    onClickHeaderImage,
    layers,
    onLayerPressed,
    currentLayerId,
    shouldShowSlider,
    sliderMinLabel,
    sliderMaxLabel,
    layerOpacity,
    layerOpacityChanged,
    adRefreshActionOnOpacitySliderChangeComplete
}: MapLayerPickerType) => {
    const mapLayerListItemRefs = useRef<MapLayerListItemRefType>({});

    useEffect(() => {
        if (currentLayerId) {
            mapLayerListItemRefs.current[currentLayerId]?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }
    }, [currentLayerId]);

    const renderHeaderImage = () => {
        return (
            <HeaderImageFromComponent onClick={() => {onClickHeaderImage();}}>
                {headerImage}
            </HeaderImageFromComponent>
        );
    };

    const onOpacityChanged = (value: number | number[]) => {
        if (layerOpacityChanged && value !== layerOpacity) {
            layerOpacityChanged(value);
        }
    };

    const renderSlider = () => {
        return (
            <SliderContainer>
                <RightSliderLabel>
                    {sliderMinLabel}
                </RightSliderLabel>
                <Slider
                    style={{ width: 100 }}
                    min={0}
                    max={100}
                    value={layerOpacity}
                    onAfterChange={() => {
                        adRefreshActionOnOpacitySliderChangeComplete(layerOpacity);
                    }}
                    onChange={onOpacityChanged}
                    trackStyle={{ backgroundColor: '#2E98E3' }}
                    railStyle={{ backgroundColor: '#555' }}
                    handleStyle={{
                        height: 24,
                        width: 24,
                        marginTop: -8,
                        marginLeft: -10,
                        border: '1px solid #555'
                    }}
                />
                <LeftSliderLabel>
                    {sliderMaxLabel}
                </LeftSliderLabel>
            </SliderContainer>
        );
    };

    const renderMapLayerListItem = (layer: MapLayerType) => {
        const IconComponent = IconHelper[layer?.iconResourceId];

        return IconComponent && <MapLayerListItem
            ref={layerElt => mapLayerListItemRefs.current[layer.id] = layerElt}
            key={layer.id}
            layerId={layer?.id}
            currentLayerId={currentLayerId}
            layerLabel={layer?.label}
            IconComponent={IconComponent}
            onLayerPressed={onLayerPressed}
        />;

    };

    return (
        <MapLayerPickerControlContainer id='mapLayerPickerControlContainer'>
            <MapLayerPickerControlHeader>
                Weather Overlay {renderHeaderImage()}
            </MapLayerPickerControlHeader>
            {shouldShowSlider && renderSlider()}
            <MapLayerPickerControlBody>
                <MapLayerList>
                    {layers?.length > 0 && layers?.map(layer => {
                        const mapLayerListItem = renderMapLayerListItem(layer);
                        return mapLayerListItem;
                    })}
                </MapLayerList>
            </MapLayerPickerControlBody>
        </MapLayerPickerControlContainer>
    );
});

const SliderContainer = styled.div`
    min-height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-left: 15px;
    padding-right: 15px;
    margin: 0px;
`;

const SliderLabel = styled.label`
    color: #aaa;
    font-size: 15px;
`;

const RightSliderLabel = styled(SliderLabel)`
    margin-right: 15px;
`;

const LeftSliderLabel = styled(SliderLabel)`
    margin-left: 15px;
`;

const MapLayerPickerControlContainer = styled.div`
    width: 239px;
    background-color: #FFFFFF;
`;

const MapLayerPickerControlHeader = styled.h3`
    text-transform: uppercase;
    font-size: 18px;
    font-weight: 600;
    color: #555;
    font-family: 'Source Sans Pro', sans-serif;
    padding-left: 15px;
    margin: 0px;
    border-bottom: 1px solid #D8D8D8;
    padding-bottom: 2px;
    padding-top: 2px;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const MapLayerPickerControlBody = styled.div`
    overflow-y: scroll;
    max-height: 202px;
`;

const MapLayerList = styled.ul`
    list-style: none;
    padding: 0px;
    color: #555;
    font-family: 'Source Sans Pro', sans-serif;
    font-weight: bold;
    font-size: 13px;
`;

const HeaderImageSharedStyle = css`
    display: flex;
    min-height: 48px;
    min-width: 48px;
    height: 100%;   
    justify-content: center;
    cursor: pointer;
    align-items: center;
    padding-right: 9px;
    &:hover {
        background-color: #EEEEEE;
    }
`;

const HeaderImageFromComponent = styled.div`
    ${HeaderImageSharedStyle}
`;
